import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Grid, Card, CardMedia
} from '@mui/material';
import { Refresh, CheckCircle, Cancel, Visibility, FilterList, Event } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const SanataniEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;

      const res = await axios.get(`${API_BASE}/admin/events`, { 
        params, 
        headers: getAuthHeaders() 
      });

      if (res.data.success) {
        setEvents(res.data.data.events || res.data.data || []);
        setTotalPages(res.data.data.totalPages || 1);
      }
    } catch (err) {
      setError('Error fetching events: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [page, statusFilter]);

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setViewDialogOpen(true);
  };

  const handleActionClick = (event, action) => {
    setSelectedEvent(event);
    setActionType(action);
    setRejectionReason('');
    setActionDialogOpen(true);
  };

  const confirmAction = async () => {
    try {
      await axios.put(
        `${API_BASE}/admin/events/${selectedEvent._id}/status`,
        { 
          status: actionType,
          rejectionReason: actionType === 'rejected' ? rejectionReason : undefined
        },
        { headers: getAuthHeaders() }
      );
      setActionDialogOpen(false);
      setSuccess(`Event ${actionType === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchEvents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error updating event: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
          📅 Sanatani Manch Events
        </Typography>
        <IconButton onClick={fetchEvents} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" startIcon={<FilterList />} onClick={fetchEvents}>
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FF9800' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Organizer</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date & Time</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Location</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event._id} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Event color="primary" />
                        {event.title}
                      </Box>
                    </TableCell>
                    <TableCell>{event.profileId?.name || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={event.eventType || 'general'} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(event.startDate)}</TableCell>
                    <TableCell>{event.location?.address || event.venue || 'Online'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={event.status || 'pending'}
                        size="small"
                        color={getStatusColor(event.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleViewEvent(event)} title="View">
                        <Visibility />
                      </IconButton>
                      {event.status === 'pending' && (
                        <>
                          <IconButton 
                            color="success" 
                            onClick={() => handleActionClick(event, 'approved')}
                            title="Approve"
                          >
                            <CheckCircle />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleActionClick(event, 'rejected')}
                            title="Reject"
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No events found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, gap: 1 }}>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Typography sx={{ alignSelf: 'center' }}>Page {page} of {totalPages}</Typography>
            <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </Box>
        </Paper>
      )}

      {/* View Event Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF9800', color: 'white' }}>
          Event Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedEvent && (
            <Grid container spacing={2}>
              {selectedEvent.bannerImage && (
                <Grid item xs={12}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="200"
                      image={selectedEvent.bannerImage}
                      alt={selectedEvent.title}
                    />
                  </Card>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{selectedEvent.title}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><b>Organizer:</b> {selectedEvent.profileId?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><b>Event Type:</b> {selectedEvent.eventType || 'General'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><b>Start:</b> {formatDate(selectedEvent.startDate)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography><b>End:</b> {formatDate(selectedEvent.endDate)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography><b>Location:</b> {selectedEvent.location?.address || selectedEvent.venue || 'Online'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography><b>Description:</b></Typography>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  {selectedEvent.description || 'No description'}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Chip 
                  label={`Status: ${selectedEvent.status || 'pending'}`}
                  color={getStatusColor(selectedEvent.status)}
                />
                <Chip label={`Attendees: ${selectedEvent.attendeeCount || 0}`} sx={{ ml: 1 }} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle sx={{ color: actionType === 'approved' ? '#4caf50' : '#f44336' }}>
          {actionType === 'approved' ? 'Approve Event' : 'Reject Event'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to {actionType === 'approved' ? 'approve' : 'reject'} event: 
            <b> {selectedEvent?.title}</b>?
          </Typography>
          {actionType === 'rejected' && (
            <TextField
              fullWidth
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={2}
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color={actionType === 'approved' ? 'success' : 'error'}
            onClick={confirmAction}
          >
            {actionType === 'approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SanataniEvents;
